import { Injectable, NgZone, inject } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { ScreenTypeService } from './screen-type.service';

interface Notification {
  message: string;
  config: MatSnackBarConfig;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly snackBar = inject(MatSnackBar);
  private readonly zone = inject(NgZone);
  private screenTypeService = inject(ScreenTypeService);

  default(message: string, duration: number = 4000) {
    this.show(message, {
      duration: duration,
      panelClass: 'default-notification-overlay',
    });
  }

  info(message: string, duration: number = 4000) {
    this.show(message, {
      duration: duration,
      panelClass: 'info-notification-overlay',
    });
  }

  success(message: string, duration: number = 4000) {
    this.show(message, {
      duration: duration,
      panelClass: 'success-notification-overlay',
    });
  }

  warn(message: string, duration: number = 5000) {
    this.show(message, {
      duration: duration,
      panelClass: 'warning-notification-overlay',
    });
  }

  error(message: string, duration: number = 6000) {
    this.show(message, {
      duration: duration,
      panelClass: 'error-notification-overlay',
    });
  }

  getSnackBarRefInstance() {
    return this.snackBar._openedSnackBarRef?.instance;
  }

  private notificationQueue: Notification[] = [];

  private show(message: string, configuration: MatSnackBarConfig) {
    configuration.horizontalPosition = this.screenTypeService.isVeryLargeScreen() ? 'start' : 'center';

    if (!this.snackBar._openedSnackBarRef) {
      // Need to open snackBar from Angular zone to prevent issues with its position per
      // https://stackoverflow.com/questions/50101912/snackbar-position-wrong-when-use-errorhandler-in-angular-5-and-material
      this.zone.run(() => this.snackBar.open(message, null, configuration));

      return;
    }

    this.notificationQueue.push({ message, config: configuration });
    this.snackBar._openedSnackBarRef.afterDismissed().subscribe(() => {
      const nextNotification = this.notificationQueue.shift();
      this.show(nextNotification.message, nextNotification.config);
    });
  }
}
